import React, {useEffect, useState} from 'react';
import {handicaddieSelector} from "../../../services/store/asyncThunk";
import {PrepaidBagProduct, PrepaidBagProductService} from "../../../services/api/PrepaidBagProductService";
import {Club, ClubService} from "../../../services/api/ClubService";
import HandicaddieLogo from "../../../icons/HandicaddieLogo";
import Container from "../../../components/Container";
import Spinner from "../../../components/Spinner";
import {SessionState} from "../../../services/store/reducers/session/slice";
import TickIcon from "../../../icons/TickIcon";
import {IconSize} from "../../../services/IconConstants";
import {useNavigate} from "react-router-dom";
import {buildUrl, PLATFORM_HOME} from "../../../services/PageUtils";
import CrossIcon from "../../../icons/CrossIcon";
import Button from "../../../components/Button";

interface Props {

}

const CompletedPaymentStatus = ({}: Props) => {

    const { user, sessionState } = handicaddieSelector((state) => state.session)

    const nav = useNavigate();

    const { getPrepaidBagProduct, getAndRectifyPurchase } = PrepaidBagProductService();
    const { getClub } = ClubService();

    const searchParams = new URLSearchParams(window.location.search);
    const purchaseId = searchParams.get('purchaseId');

    const [loading, setLoading] = useState<boolean>(true)
    const [inProgress, setInProgress] = useState<boolean>(false)

    const [wasSuccessful, setWasSuccessful] = useState<boolean>(false);
    const [club, setClub] = useState<Club | undefined>();
    const [product, setProduct] = useState<PrepaidBagProduct | undefined>();

    const [remainingTime, setRemainingTime] = useState<number>(5);
    const [timerStarted, setTimerStarted] = useState<boolean>(false);

    useEffect(() => {
        setClub(undefined)
        setProduct(undefined)

        if (sessionState !== SessionState.ACTIVE || !user) {
            return;
        }

        reloadPurchase()
    }, [user, purchaseId])

    useEffect(() => {
        let interval: NodeJS.Timeout | undefined;

        if (timerStarted) {
            interval = setInterval(() => {
                setRemainingTime(prev => prev - 1);
            }, 1000);
        }

        return () => {
            if (interval) clearInterval(interval);
        };
    }, [timerStarted]); // start interval when timerStarted becomes true

    useEffect(() => {
        if (remainingTime <= 0) {
            goHome(); // or whatever navigation you want
        }
    }, [remainingTime]);

    const reloadPurchase = () => {
        if (purchaseId) {
            if (!inProgress) {
                loadPurchase(purchaseId)
            }
            return;
        } else {
            setWasSuccessful(false)
            return;
        }
    }

    const loadPurchase = async (id: string, attempt: number = 1) => {
        setLoading(true);
        setInProgress(true);
        try {
            let resp = await getAndRectifyPurchase(id);
            if (resp.error) {
                throw new Error('Purchase load failed');
            }
            if (resp.purchase && resp.purchase.status) {
                if (resp.purchase.status === 'FAILED') {
                    setWasSuccessful(false);
                    setLoading(false);
                    return;
                }

                let productResp = await getPrepaidBagProduct(resp.purchase.productId);
                if (productResp.error) {
                    throw new Error('Product load failed');
                }

                if (productResp.product) {
                    let clubResp = await getClub(productResp.product.clubId);
                    if (clubResp.error) {
                        throw new Error('Club load failed');
                    }

                    if (clubResp.club) {
                        setProduct(productResp.product);
                        setClub(clubResp.club);
                        setWasSuccessful(true);
                        setLoading(false);
                        setTimerStarted(true);
                        return;
                    }
                }
            }
            throw new Error('Unexpected error');
        } catch (error) {
            if (attempt < 5) {
                setTimeout(() => {
                    loadPurchase(id, attempt + 1);
                }, 2000); // wait 2 seconds
            } else {
                setWasSuccessful(false);
                setLoading(false)
            }
        }
    };

    const goHome = () => {
        nav(buildUrl(PLATFORM_HOME), { replace: true });
    }

    return (
        <Container>
            <div style={{height: '100%', display: 'flex', flexDirection: 'column'}}>
                <div className={"header_logoContainer"}>
                    <HandicaddieLogo size={'lg'}/>
                </div>

                {loading && <div style={{ display: 'flex', alignItems: 'center', flexDirection: 'column' }}>
                    <Spinner onBackground />
                    <p>Completing your payment...</p>
                </div>}

                {!loading && wasSuccessful && product && club && <div style={{ display: 'flex', alignItems: 'center', flexDirection: 'column' }}>
                    <TickIcon size={IconSize.bagXl} />
                    <h4>{product.name} added for {club.name}</h4>
                    <p>Your payment was successful. Please allow a few minutes for your balance to be updated on the app.</p>
                    <br />
                    <br />
                    <p>Redirecting you to the dashboard in {remainingTime} seconds...</p>
                </div>}

                {!loading && !wasSuccessful && <div style={{ display: 'flex', alignItems: 'center', flexDirection: 'column' }}>
                    <CrossIcon size={IconSize.bagXl} colour={'var(--color-error)'}/>
                    <p>Your payment was not successful. Your balance will not be updated.</p>
                    <p>If you think this is an error, don't panic, just reach out to us on <code>help@handicaddie.com</code> and we can look into it.</p>
                    <br />
                    <br />
                    <div style={{ display: 'flex', alignItems: 'center', flexDirection: 'column', width: '100%', gap: '1em' }}>
                        <Button text={"GO HOME"} onClick={goHome} />
                        <Button variant={'secondary'} text={"RETRY"} onClick={reloadPurchase} />
                    </div>
                </div>}
            </div>
        </Container>
    )
}

export default CompletedPaymentStatus;